import { type FC, useEffect, useState } from 'react';

import {
  Button,
  Circle,
  Flex,
  HStack,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Progress,
  Stack,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { type UseFormReturn } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import {
  IconCloudUploadBrand,
  IconTrashEmpty,
  IconUploaded,
} from '../../components/Icons';
import { UploadDropZone } from '../../components/UploadDropZone';
import { useUploadFileMutation } from '../file/useUploadFileMutation';
import { type NewInterviewFormInputs } from './common';

type QuestionMediaModalProps = {
  isOpen: boolean;
  onClose: () => void;
  form: UseFormReturn<NewInterviewFormInputs>;
  index: number;
  isEditMode: boolean;
};

export const QuestionMediaModal: FC<QuestionMediaModalProps> = ({
  isOpen,
  onClose,
  form,
  isEditMode,
  index,
}) => {
  const { t } = useTranslation([
    'common',
    'cv',
    'components',
    'interview',
    'candidate',
  ]);
  const [isDeleted, setIsDeleted] = useState(false);
  const [rawFile, setRawFile] = useState<File | undefined>();
  const [uploadedFileKey, setUploadedFileKey] = useState<string>();

  const fileUploadMutation = useUploadFileMutation({
    onSuccess(uploadRes) {
      setUploadedFileKey(uploadRes.fileKey ?? undefined);
    },
    onError() {
      toast({ status: 'error', title: t('common:upload.uploadError') });
    },
  });

  const toast = useToast();

  const onUpload = () => {
    const isAudio = rawFile?.type.includes('audio');
    form.setValue(`questions.${index}.file`, rawFile);
    if (isAudio) {
      form.setValue(
        `questions.${index}.mediaFileKeys`,
        uploadedFileKey ? [uploadedFileKey] : []
      );
    } else {
      form.setValue(
        `questions.${index}.imageFileKeys`,
        uploadedFileKey ? [uploadedFileKey] : []
      );
    }
    onClose();
  };

  useEffect(() => {
    if (!form.getValues(`questions.${index}.file`)) {
      setRawFile(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.getValues(`questions.${index}.file`)]);

  return (
    <Modal isCentered isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent borderRadius="2xl">
        <ModalCloseButton />

        <ModalBody alignItems="center" justifyContent="center" my={4}>
          <VStack gap={3}>
            <VStack spacing={0}>
              <Text fontWeight="semibold" fontSize="xl">
                {t('candidate:importByExcel.upload')}
              </Text>
              <Text color="gray.600">
                {t(
                  'interview:createNewInterview.questionPreparation.questionMediaModalDescription'
                )}
              </Text>
            </VStack>
            {isEditMode &&
            !rawFile &&
            !isDeleted &&
            form.getValues(`questions.${index}.imageFileKeys`)?.[0] ? (
              <Stack minH={200}>
                <Image
                  src={form.getValues(`questions.${index}.imageFileKeys`)![0]}
                />
              </Stack>
            ) : (
              <UploadDropZone
                height={124}
                mt={3}
                w="full"
                cursor="pointer"
                onDrop={async ([file]) => {
                  if (file) {
                    console.log({ file });
                    const validTypes = [
                      'image/png',
                      'image/jpg',
                      'image/jpeg',
                      'audio/mpeg',
                    ];
                    if (!validTypes.includes(file.type)) {
                      toast({
                        status: 'error',
                        title: t(
                          'components:emailSettings.invalidFileTypeForQuestionMedia'
                        ),
                      });
                      return;
                    }
                  }

                  if (file?.size && file?.size / (1024 * 1024) > 5) {
                    return toast({
                      title: t(
                        'interview:createNewInterview.questionPreparation.maximumImageSize'
                      ),
                      status: 'error',
                    });
                  }

                  setRawFile(file);
                  await fileUploadMutation.mutateAsync({
                    access: {
                      contentType: file!.type,
                    },
                    file: file!,
                  });
                }}
                rounded="xl"
                dropZoneOptions={{
                  accept: {
                    'image/*': ['.png', '.jpg', '.jpeg'],
                    'audio/*': ['.mp3'],
                  },
                  multiple: false,
                }}
              >
                <Circle size="40px" bg="brand.50" fontSize={20}>
                  <IconCloudUploadBrand />
                </Circle>

                <VStack rowGap={0}>
                  <Trans
                    t={t}
                    parent={(props) => (
                      <Text textAlign="center" as="div" {...props} />
                    )}
                    i18nKey="interview:mediaQuestionInstruction"
                    components={[
                      <Text
                        color="brand.700"
                        fontSize="md"
                        fontWeight="medium"
                        as="span"
                        key="0"
                      />,
                      <Text as="span" color="gray.500" fontSize="md" key="1" />,
                      <Text fontSize="sm" color="gray.500" key="2" />,
                    ]}
                  />
                </VStack>
              </UploadDropZone>
            )}
            {rawFile && (
              <Stack
                borderRadius={12}
                borderColor="#CFBAE9"
                height={16}
                borderWidth={1}
                width="full"
                padding={2}
                alignItems="center"
                marginTop={3}
                direction="row"
                position="relative"
              >
                <Flex
                  justifyContent="center"
                  alignItems="center"
                  backgroundColor="#FCF9FF"
                  width={10}
                  borderColor="#F0E9F8"
                  borderWidth="1px"
                  height={12}
                  borderRadius={8}
                >
                  <IconUploaded fontSize={24} />
                </Flex>
                <Flex
                  paddingY={1}
                  justifyContent="space-between"
                  alignItems="space-between"
                  direction="column"
                  flex={1}
                  height="full"
                >
                  {rawFile && (
                    <Text fontSize={{ base: 12, sm: 'sm' }}>
                      {rawFile.name + ' ' + getFileSize(rawFile)}
                    </Text>
                  )}
                  <Progress
                    borderRadius={3}
                    width="full"
                    size="sm"
                    value={fileUploadMutation.uploadProgress}
                  />
                </Flex>
                <IconTrashEmpty
                  onClick={() => {
                    setRawFile(undefined);
                    setUploadedFileKey(undefined);
                  }}
                  cursor="pointer"
                  position="absolute"
                  top={1.5}
                  right={3}
                />
                <Text
                  width={{ base: '12%', sm: '10%' }}
                  fontSize={{ base: 'xs', md: 'sm' }}
                  alignSelf="end"
                  textAlign="center"
                >
                  {fileUploadMutation.uploadProgress + '%'}
                </Text>
              </Stack>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter pt={0}>
          <HStack gap={3} w="full">
            {isEditMode &&
            !rawFile &&
            form.getValues(`questions.${index}.imageFileKeys`)?.[0] ? (
              <Button
                onClick={() => {
                  setRawFile(undefined);
                  setUploadedFileKey(undefined);
                  form.setValue(`questions.${index}.imageFileKeys`, []);
                  setIsDeleted(true);
                }}
                w="full"
                variant="outline"
                color="red.400"
              >
                Delete
              </Button>
            ) : (
              <Button
                isLoading={fileUploadMutation.isPending}
                flexGrow={1}
                variant="@primary"
                disabled={
                  (!uploadedFileKey &&
                    !form.getValues(`questions.${index}.file`)) ||
                  fileUploadMutation.isPending
                }
                onClick={onUpload}
              >
                {form.getValues(`questions.${index}.file`)
                  ? t('common:actions.save')
                  : t('candidate:importByExcel.upload')}
              </Button>
            )}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const getFileSize = (file: File) => {
  if (file) {
    const fileSizeInBytes = file.size;
    const fileSizeInKB = fileSizeInBytes / 1024; // Convert to KB
    const fileSizeInMB = fileSizeInBytes / (1024 * 1024); // Convert to MB

    if (fileSizeInMB >= 1) {
      return `${fileSizeInMB.toFixed(2)} MB`;
    }
    return `${fileSizeInKB.toFixed(2)} KB`;
  }
};
